import { useCallback, useRef } from "react";
import styled from "styled-components";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import MovieCard from "../MovieCard";

const Wrapper = styled.section`
  display: grid;
  gap: 1rem;
  width: min(1280px, calc(100% - 1.5rem));
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 1rem;
`;

const Copy = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(1.35rem, 2vw, 1.9rem);
`;

const Description = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.95rem;
`;

const Controls = styled.div`
  display: flex;
  gap: 0.6rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const ScrollButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.white};
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Track = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(200px, 18vw);
  gap: 1rem;
  overflow-x: auto;
  padding-bottom: 0.3rem;
  scroll-snap-type: x proximity;
  scroll-behavior: smooth;

  & > * {
    scroll-snap-align: start;
  }

  &::-webkit-scrollbar {
    height: 8px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-auto-columns: minmax(190px, 22vw);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-auto-columns: minmax(160px, 42vw);
  }
`;

function MovieRow({ title, description, items = [], onSelectMovie }) {
  const trackRef = useRef(null);

  const handleScroll = useCallback((direction) => {
    if (!trackRef.current) {
      return;
    }

    const amount = direction === "left" ? -trackRef.current.clientWidth * 0.8 : trackRef.current.clientWidth * 0.8;
    trackRef.current.scrollBy({ left: amount, behavior: "smooth" });
  }, []);

  if (!items.length) {
    return null;
  }

  return (
    <Wrapper>
      <Header>
        <Copy>
          <Title>{title}</Title>
          {description ? <Description>{description}</Description> : null}
        </Copy>
        <Controls aria-label={`Controles da lista ${title}`}>
          <ScrollButton type="button" onClick={() => handleScroll("left")} aria-label={`Rolar ${title} para a esquerda`}>
            <BsChevronLeft />
          </ScrollButton>
          <ScrollButton type="button" onClick={() => handleScroll("right")} aria-label={`Rolar ${title} para a direita`}>
            <BsChevronRight />
          </ScrollButton>
        </Controls>
      </Header>
      <Track ref={trackRef}>
        {items.map((movie) => (
          <MovieCard key={movie.id} movie={movie} onSelect={onSelectMovie} />
        ))}
      </Track>
    </Wrapper>
  );
}

export default MovieRow;
